import { LinkOpenIcon, type TypeTableColumn } from '@thefarmersfront/kitchen';

import { TextButtonCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  DELIVERY_AGENCY_COURIER_CODE,
  DELIVERY_AGENCY_SHIPPING_LABEL_STATUS,
  DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR,
  DELIVERY_AGENCY_TRACKING_LINKS,
} from '~/constants';

import { StatusBadge } from './deliveryAgencyTableCells';

import type { TypeDeliveryAgencyShippingLabelDetail } from '~/types/delivery-agency.types';

import styles from './getDeliveryAgencyTrackingShippingLabelInfoColumnDefs.module.scss';

type TypeShippingLabelInfo = TypeDeliveryAgencyShippingLabelDetail['shippingLabelInfo'];

const getDeliveryAgencyTrackingShippingLabelInfoColumnDefs = () => {
  const columnDefs: TypeTableColumn<TypeShippingLabelInfo>[] = [
    {
      accessorKey: 'shippingLabelNumber',
      header: '운송장 번호',
      size: 245,
    },
    {
      accessorKey: 'status',
      header: '운송장 상태',
      size: 262,
      cell: ({ row }) => {
        const { status: value, deliveryCancelReason } = row.original;
        if (!value) return null;

        const showCancelReason =
          value.code === DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED &&
          deliveryCancelReason?.code === 'OUTBOUND_RESET';

        return (
          <div className={styles['status-cell']}>
            <StatusBadge
              value={value}
              colors={DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR}
              dashIfNoLabel={false}
            />
            {showCancelReason && <span>({deliveryCancelReason?.description})</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'statusUpdatedAt',
      header: '상태 마지막 업데이트 일시',
      size: 170,
      cell: ({ row }) => formatDate(row.original.statusUpdatedAt, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      id: 'deliveryType',
      accessorFn: (row) => row.deliveryType?.description,
      header: '배송 유형',
      size: 80,
    },
    {
      id: 'courierCode',
      accessorFn: (row) => row.courierCode?.description,
      header: '배송사',
      size: 88,
    },
    {
      id: 'trackingLink',
      accessorKey: 'shippingLabelNumber',
      header: '',
      size: 150,
      cell: ({ getValue, row }) => {
        const courierCode = row.original.courierCode.code as keyof typeof DELIVERY_AGENCY_COURIER_CODE | undefined;
        const invoiceNumber = row.original.shippingLabelNumber;
        const url = courierCode && invoiceNumber ? DELIVERY_AGENCY_TRACKING_LINKS[courierCode] + invoiceNumber : '';

        return (
          <TextButtonCell
            value={getValue()}
            variant="normal"
            target="_blank"
            href={url}
            rightIcon={<LinkOpenIcon size={16} color="#672091" />}
          >
            배송조회
          </TextButtonCell>
        );
      },
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyTrackingShippingLabelInfoColumnDefs;
